/**
 * Cart discount code control. Applies a code via /discount/{code}, then reads
 * /cart.js to see whether it discounts the cart directly: only then does it ask
 * <cart-items> to re-render (which replaces this element). A code merely accepted
 * for checkout gets a note instead, so the note survives. The element sits inside
 * the cart <form>, so the input has no name and Enter is intercepted here, or
 * Enter would submit that form and jump to checkout.
 */

if (!customElements.get('cart-discount')) {
	class CartDiscount extends HTMLElement {
		constructor() {
			super();
			this.input = this.querySelector('.cart-block-discount__input');
			this.button = this.querySelector('.cart-block-discount__apply');
			this.messageEl = this.querySelector('.cart-block-discount__message');
			if (this.button) {
				this.button.addEventListener('click', this.apply.bind(this));
			}
			if (this.input) {
				this.input.addEventListener('keydown', (event) => {
					if (event.key === 'Enter') {
						event.preventDefault();
						this.apply();
					}
				});
			}
			// Delegated: the chips are server-rendered and replaced on every re-render.
			this.addEventListener('click', (event) => {
				const trigger = event.target.closest('[data-discount-remove]');
				if (!trigger) {
					return;
				}
				event.preventDefault();
				this.removeCode(trigger.dataset.discountRemove);
			});
		}

		connectedCallback() {
			this.showInactiveCodes();
		}

		// Runs per re-render, since the innerHTML swap builds a fresh element.
		async showInactiveCodes() {
			const template = this.querySelector('.cart-block-discount__inactive-template');
			if (!template) {
				return;
			}
			let cart;
			try {
				cart = await window.UFT.Cart.getState();
			} catch (error) {
				console.error(error);
				return;
			}
			// The swap may have replaced this element while the request was in flight.
			if (!this.isConnected) {
				return;
			}
			// Idempotent: connectedCallback runs this once, and handleUnappliedCode runs it again
			// on a surface that deliberately did not re-render, so the chips it added last time
			// are still here. Only this method's own chips are cleared; the server-rendered ones
			// are for applied codes, which unchippedCodes excludes by definition.
			this.querySelectorAll('[data-inactive-chip]').forEach((chip) => chip.remove());
			const unchipped = this.unchippedCodes(cart);
			if (unchipped.length) {
				this.appendInactiveChips(template, unchipped);
			}
		}

		// Codes the cart holds but isn't applying: applicable:false (Shopify keeps a non-combinable or rejected code
		// rather than dropping it) and applicable:true with no matching application (accepted for checkout only, e.g.
		// free shipping). Neither has a discount application, so the server-rendered chips can't cover them.
		unchippedCodes(cart) {
			const appliedTitles = this.appliedCodeTitles(cart);
			return (cart.discount_codes || []).filter(
				(entry) => entry.applicable === false || !appliedTitles.includes((entry.code || '').toUpperCase())
			);
		}

		appendInactiveChips(template, entries) {
			const list = this.inactiveChipList();
			entries.forEach((entry) => {
				const note = entry.applicable === false ? this.dataset.inactiveChipNote : this.dataset.pendingChipNote;
				const chip = this.buildInactiveChip(template, entry.code, note);
				// Marks it as this method's own, so a second run replaces rather than duplicates.
				chip.dataset.inactiveChip = '';
				list.appendChild(chip);
			});
		}

		// The applied list only exists once a code applied, so an inactive-only cart needs one created.
		inactiveChipList() {
			const existing = this.querySelector('.cart-block-discount__applied');
			if (existing) {
				return existing;
			}
			const list = document.createElement('ul');
			list.className = 'cart-block-discount__applied';
			this.querySelector('.cart-block-discount__row')?.before(list);
			return list;
		}

		// The remove control needs no listener of its own; the delegated one in the constructor catches it.
		buildInactiveChip(template, code, note) {
			const chip = template.content.firstElementChild.cloneNode(true);
			chip.querySelector('.cart-block-discount__applied-code').textContent = code;
			const noteEl = chip.querySelector('.cart-block-discount__applied-note');
			if (noteEl) {
				noteEl.textContent = note || '';
			}
			const remove = chip.querySelector('[data-discount-remove]');
			remove.dataset.discountRemove = code;
			// The same per-code scheme the server-rendered chips use (see cart-block-discount.liquid).
			// The template carries no focus key of its own, so without this an inactive chip's removal
			// captured nothing and the re-render restored focus nowhere.
			if (this.dataset.removeFocusPrefix) {
				remove.dataset.focusKey = `${this.dataset.removeFocusPrefix}-${code}`;
			}
			remove.setAttribute('aria-label', `Remove discount ${code}`);
			return chip;
		}

		// The announcement is one-shot into a capped queue, so the field itself must still report its state:
		// aria-describedby points at the message, and aria-invalid is set on the error branch only (WCAG 3.3.1, 4.1.2).
		showMessage(text, isError = false) {
			if (this.messageEl) {
				this.messageEl.textContent = text;
				this.messageEl.hidden = text === '';
			}
			if (this.input) {
				if (isError) {
					this.input.setAttribute('aria-invalid', 'true');
				} else {
					this.input.removeAttribute('aria-invalid');
				}
			}
			if (text) {
				window.UFT?.CartStatusAnnouncer?.announce?.(text);
			}
		}

		setLoading(isLoading) {
			if (this.button) {
				this.button.disabled = isLoading;
			}
			if (this.input) {
				this.input.disabled = isLoading;
			}
			this.querySelectorAll('[data-discount-remove]').forEach((control) => {
				control.disabled = isLoading;
			});
		}

		async removeCode(code) {
			// Before setLoading: disabling the focused chip button unfocuses it, leaving the re-render nothing to restore.
			const focusKey = CartItems.activeFocusKey();
			this.setLoading(true);
			this.showMessage('');
			try {
				const cart = await window.UFT.Cart.getState();
				const survivors = (cart.discount_codes || [])
					.map((entry) => entry.code)
					.filter((entry) => entry.toUpperCase() !== code.toUpperCase());
				// A throttled or rejected update must not be announced as a removal while the re-render redraws the code.
				const { ok, state } = await window.UFT.Cart.setDiscounts(survivors);
				if (!ok) {
					this.showMessage(window.UFT.Cart.errorMessage(state, 'We could not remove that code. Please try again.'));
					this.setLoading(false);
					return;
				}
				window.UFT?.CartStatusAnnouncer?.announce?.('Discount removed.');
				this.rerenderOrRelease(focusKey);
			} catch (error) {
				console.error(error);
				this.showMessage('Something went wrong. Please try again.');
				this.setLoading(false);
			}
		}

		// A successful re-render replaces this element, so the loading state dies with it. The code is already off the cart,
		// so a failed repaint leaves the discount rows and total describing a cart that no longer exists.
		rerenderOrRelease(focusKey) {
			const cartItems = this.closest('cart-items');
			if (!cartItems) {
				this.setLoading(false);
				return;
			}
			cartItems.rerenderFromFetch(focusKey).catch(() => {
				this.setLoading(false);
				cartItems.recoverFromStaleCart();
			});
		}

		async apply() {
			const code = (this.input?.value || '').trim();
			if (!code) {
				return;
			}
			// Before setLoading: disabling the focused input or button unfocuses it, leaving nothing to restore.
			const focusKey = CartItems.activeFocusKey();
			this.setLoading(true);
			this.showMessage('');
			try {
				await window.UFT.Cart.applyDiscount(code);
				const cart = await window.UFT.Cart.getState();
				if (this.applyToCartIfDiscounted(cart, code, focusKey)) {
					return;
				}
				await this.handleUnappliedCode(cart, code);
			} catch (error) {
				console.error(error);
				this.settleWithMessage('Something went wrong. Please try again.', true);
			}
		}

		// Show the note without re-rendering so it persists.
		//
		// The code is NOT discarded. applicable:false covers a typo, but it equally covers a code
		// below its minimum spend and one that will not combine with a code already on the cart,
		// and /cart.js carries nothing that separates them. Discarding on that flag deleted a real
		// promo the moment a shopper entered it a few dollars early, told them it was invalid, and
		// left adding the qualifying product doing nothing. Held on the cart instead, Shopify
		// applies it by itself as soon as it qualifies, which is the outcome the shopper wanted.
		// The chip refresh below is what keeps it removable meanwhile.
		async handleUnappliedCode(cart, code) {
			const held = this.isCodeOnCart(cart, code);
			this.settleWithMessage(this.messageForUnappliedCode(cart, code), !held);
			// Chips are built server-side and on connect, and this path deliberately does not
			// re-render, so without this the code the shopper just entered has no remove control
			// until some unrelated mutation swaps the element.
			await this.showInactiveCodes();
		}

		// Refocuses the input, which the disabled state dropped to <body>.
		settleWithMessage(message, isError) {
			this.showMessage(message, isError);
			this.setLoading(false);
			this.input?.focus();
		}

		// Both levels have to be read: a product- or collection-scoped code lands ONLY in
		// items[].line_level_discount_allocations. And it is cart_level_discount_applications, NOT the Liquid cart's
		// discount_applications, which the AJAX body doesn't carry at all.
		appliedCodeTitles(cart) {
			const titles = [];
			const collect = (application) => {
				if (application?.type === 'discount_code' && application.title) {
					titles.push(application.title.toUpperCase());
				}
			};
			(cart.cart_level_discount_applications || []).forEach(collect);
			(cart.items || []).forEach((item) => {
				(item.line_level_discount_allocations || []).forEach((allocation) => collect(allocation.discount_application));
			});
			return titles;
		}

		// True when the cart itself carries a matching application, i.e. the code discounts the cart right now.
		isCodeApplied(cart, code) {
			return this.appliedCodeTitles(cart).includes(code.toUpperCase());
		}

		// /discount/{code} answers 200 for a typo too and Shopify records the attempt either way, so presence in
		// discount_codes proves nothing: the applicable flag is what separates a held code from a non-existent one.
		isCodeAcceptedForCheckout(cart, code) {
			if (!Array.isArray(cart.discount_codes)) {
				return false;
			}
			return cart.discount_codes.some(
				(entry) => entry.applicable === true && (entry.code || '').toUpperCase() === code.toUpperCase()
			);
		}

		// Shopify records every attempt, typo included, so a code missing from the list entirely is
		// the one case that really is "not accepted at all", which is what invalidNote describes.
		isCodeOnCart(cart, code) {
			if (!Array.isArray(cart.discount_codes)) {
				return false;
			}
			return cart.discount_codes.some((entry) => (entry.code || '').toUpperCase() === code.toUpperCase());
		}

		// Three states, because the cart distinguishes three. Held is the one that used to be
		// reported as invalid while the code was deleted underneath it, so its wording has to
		// agree with the chip the shopper is now looking at.
		messageForUnappliedCode(cart, code) {
			if (this.isCodeAcceptedForCheckout(cart, code)) {
				return this.dataset.checkoutNote || 'This code will be applied at checkout.';
			}
			if (this.isCodeOnCart(cart, code)) {
				return this.dataset.heldNote || "That code isn't applying to this cart.";
			}
			return this.dataset.invalidNote || "That code isn't valid.";
		}

		// True only when a re-render was kicked off, so apply() knows the flow is fully handled.
		applyToCartIfDiscounted(cart, code, focusKey) {
			if (!this.isCodeApplied(cart, code)) {
				return false;
			}
			window.UFT?.CartStatusAnnouncer?.announce?.('Discount applied.');
			const cartItems = this.closest('cart-items');
			if (!cartItems) {
				return false;
			}
			// The code is already applied server-side, so a failed repaint leaves the pre-discount total on screen.
			cartItems.rerenderFromFetch(focusKey).catch(() => {
				this.setLoading(false);
				cartItems.recoverFromStaleCart();
			});
			return true;
		}
	}
	customElements.define('cart-discount', CartDiscount);
}
